/* eslint-disable @typescript-eslint/no-explicit-any */
const codeMessage = {
  200: 'The server successfully returned the requested data.',
  201: 'New or modified data was successful.',
  202: 'A request has been queued in the background (asynchronous task).',
  204: 'Deleting data was successful.',
  400: 'There was an error in the outgoing request, the server did not perform a new or modified data operation.',
  401: 'The user does not have permission (wrong token, username, password).',
  403: 'The user is authorized, but access is forbidden.',
  404: 'The request was sent for a record that does not exist and the server did not perform the operation.',
  405: 'The request method is not allowed.',
  406: 'The format of the request is not available.',
  410: 'The requested resource is permanently deleted and will not be available again.',
  417: 'Your client is out-of-date and must be updated.',
  422: 'A validation error occurred when an object was created.',
  429: 'Sorry, you seem to be doing too much too fast. Please wait one minute before trying again.',
  500: 'An unknown server error has occurred, or servers are unreachable. Try again later.',
  502: 'Gateway error.',
  503: 'Service is not available, the server is temporarily overloaded or under maintenance.',
  504: 'Gateway timeout.',
  520: 'Unknown server error.',
  521: 'Server is temporarily undergoing maintenance.',
  522: 'Connecting to server timed out.',
  523: 'Cannot reach server.',
  524: 'Timeout error.',
  525: 'Security error 525.',
  526: 'Security error 526.',
} as const;

export class AbortError extends Error {
  // special class for when AbortController fails
  constructor(error: Error) {
    console.info('AbortError: Request manually aborted', error);

    super(error.message);

    this.stack = error.stack;
    this.name = error.name;
  }
}

export class RequestError extends Error {
  data: any;
  code: number;
  message: string;

  constructor(
    data: any & { message: string },
    givenDefaultMessage: string | undefined = undefined,
  ) {
    const code: keyof typeof codeMessage = data?.code
      ? data.code
      : data?.statusCode
        ? data.statusCode
        : 500;

    const defaultMessage =
      givenDefaultMessage ||
      (code && codeMessage[code] ? codeMessage[code] : 'An error has occurred');
    const fromData = typeof data === 'string' ? data : null;
    const message = data?.message || fromData || defaultMessage;

    super(message);

    this.data = data;
    this.code = Number(code) || 500;
    this.message = message;

    if (this.constructor === RequestError && code > 404) {
      console.warn(this);
    }
  }
}

export class ClientOutdatedError extends RequestError {
  static DEFAULT_MESSAGE = codeMessage[417];

  data: {
    name: 'ClientOutdatedError';
    statusCode: 417;
    data: any;
  };

  constructor(data?: any) {
    const message = data?.message || ClientOutdatedError.DEFAULT_MESSAGE;

    super(data, message);
    this.data = data;
    this.code = Number(data?.statusCode) || this.code;
  }
}

export class ExpiredSessionError extends RequestError {
  data: {
    name: 'ExpiredSessionError';
    statusCode: 401;
    data: any;
  };

  constructor(data?: any) {
    const message = data?.message || 'Your session is expired and must be refreshed';

    super(data, message);
    this.data = data;
    this.code = Number(data?.statusCode) || this.code;
  }
}

export class NoSessionError extends RequestError {
  data: {
    name: 'NoSessionError';
    statusCode: 401;
    data: any;
  };

  constructor(data?: any) {
    const message = data?.message || 'Please sign in to access this content.';

    super(data, message);
    this.data = data;
    this.code = Number(data?.statusCode) || this.code;
  }
}

export class NotFoundError extends RequestError {
  data: {
    name: 'NotFoundError';
    statusCode: 404;
    type: string;
    data: any;
  };

  constructor(data: any) {
    const message = data?.message || codeMessage[404];

    super(data, message);
    this.data = data;
    this.code = Number(data?.statusCode) || this.code;
  }
}

export class ValidationError extends RequestError {
  data: {
    name: 'ValidationError';
    message: string;
    error: string;
    details: Record<
      'body' | 'query',
      {
        context: { label: string; key: string };
        message: string;
        path: string[];
        type: string;
      }[]
    >;
  };

  constructor(data: any & { message: string }) {
    const message = data?.details?.query?.[0]?.message || data?.message || 'ValidationError';

    super(data, message);
    this.data = data;
  }
}

export class UniqueViolationError extends RequestError {
  data: {
    name: 'UniqueViolationError';
    nativeError: {
      length: number;
      name: string;
      severity: string;
      code: string | number;
      detail: string;
      schema: string;
      table: string;
      constraint: string;
      file: string;
      line: string | number;
      routine: string;
    };
    client: string;
    table: string;
    columns: string[];
    constraint: string;
    level: string;
  };

  constructor(data: any) {
    super(data, data?.message || data?.nativeError?.detail || data?.constraint);
    const fromData = typeof data === 'string' ? data : null;

    this.data = data;
    this.message =
      data?.nativeError?.detail || data?.constraint || fromData || 'UniqueViolationError';
  }
}

export class DataError extends RequestError {
  data: {
    name: 'DataError';
    nativeError: {
      length: number;
      name: string;
      severity: string;
      code: string | number;
      file: string;
      line: string | number;
      routine: string;
    };
    client: string;
    level: string;
  };

  constructor(data: any) {
    super(data, data?.message || data?.nativeError?.detail || data?.constraint);
    const fromData = typeof data === 'string' ? data : null;

    this.data = data;
    this.message = fromData || data?.nativeError?.where || 'Data Error';
  }
}

export class DbError extends RequestError {
  data: {
    name: 'DbError';
    nativeError: {
      length: number;
      name: string;
      severity: string;
      code: string | number;
      file: string;
      line: string | number;
      routine: string;
    };
    client: string;
    level: string;
  };

  constructor(data: any) {
    super(data, data?.message || data?.nativeError?.detail || data?.constraint);
    const fromData = typeof data === 'string' ? data : null;

    this.data = data;
    this.message = fromData || data?.nativeError?.where || 'Database Error';
  }
}
